import React, { FC, useEffect, useState } from 'react';
import type { ArtistBioDetails } from '../../interfaces/artist';
import './AboutArtistModal.scss';

export interface AboutArtistModalProps {
  isOpen: boolean;
  onClose: () => void;
  details: ArtistBioDetails | null;
}

export const SpotifyVerifiedBadge = ({ size = 18 }: { size?: number }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    style={{ display: 'inline-block', verticalAlign: 'middle', flexShrink: 0 }}
  >
    <path
      d="M10.814.5a1.658 1.658 0 0 1 2.372 0l2.512 2.572 3.595-.043a1.658 1.658 0 0 1 1.678 1.678l-.043 3.595 2.572 2.512c.667.65.667 1.722 0 2.372l-2.572 2.512.043 3.595a1.658 1.658 0 0 1-1.678 1.678l-3.595-.043-2.512 2.572a1.658 1.658 0 0 1-2.372 0l-2.512-2.572-3.595.043a1.658 1.658 0 0 1-1.678-1.678l.043-3.595L.5 13.186a1.658 1.658 0 0 1 0-2.372l2.572-2.512-.043-3.595a1.658 1.658 0 0 1 1.678-1.678l3.595.043L10.814.5z"
      fill="#A3E7AC"
    />
    <path
      d="M17.398 9.62a1 1 0 0 0-1.414-1.413l-6.011 6.01-1.894-1.893a1 1 0 0 0-1.414 1.414l3.308 3.308 7.425-7.425z"
      fill="#000000"
      transform="translate(0, -0.6)"
    />
  </svg>
);

const CloseIcon = () => (
  <svg width="18" height="18" viewBox="0 0 16 16" fill="currentColor">
    <path d="M1.47 1.47a.75.75 0 0 1 1.06 0L8 6.94l5.47-5.47a.75.75 0 1 1 1.06 1.06L9.06 8l5.47 5.47a.75.75 0 1 1-1.06 1.06L8 9.06l-5.47 5.47a.75.75 0 0 1-1.06-1.06L6.94 8 1.47 2.53a.75.75 0 0 1 0-1.06z" />
  </svg>
);

const PrevIcon = () => (
  <svg width="20" height="20" viewBox="0 0 16 16" fill="currentColor">
    <path d="M11.03.47a.75.75 0 0 1 0 1.06L4.56 8l6.47 6.47a.75.75 0 1 1-1.06 1.06L2.44 8 9.97.47a.75.75 0 0 1 1.06 0z" />
  </svg>
);

const NextIcon = () => (
  <svg width="20" height="20" viewBox="0 0 16 16" fill="currentColor">
    <path d="M4.97.47a.75.75 0 0 0 0 1.06L11.44 8l-6.47 6.47a.75.75 0 1 0 1.06 1.06L13.56 8 6.03.47a.75.75 0 0 0-1.06 0z" />
  </svg>
);

const ExternalLinkIcon = () => (
  <svg width="14" height="14" viewBox="0 0 16 16" fill="currentColor">
    <path d="M3 1a1 1 0 0 0-1 1v12a1 1 0 0 0 1 1h10a1 1 0 0 0 1-1V9a.75.75 0 0 0-1.5 0v5H3.5V2.5H8a.75.75 0 0 0 0-1.5H3zm7 0a.75.75 0 0 0 0 1.5h2.94L7.22 8.22a.75.75 0 0 0 1.06 1.06L14 3.56V6.5a.75.75 0 0 0 1.5 0V1a1 1 0 0 0-1-1h-5.5z" />
  </svg>
);

export const AboutArtistModal: FC<AboutArtistModalProps> = ({ isOpen, onClose, details }) => {
  const [activeImageIndex, setActiveImageIndex] = useState(0);

  useEffect(() => {
    setActiveImageIndex(0);
    // Preload all carousel images immediately in the background
    if (details?.images && details.images.length > 0) {
      details.images.forEach((src) => {
        const img = new Image();
        img.src = src;
      });
    }
  }, [details?.artistId, details?.images]);

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };
    if (isOpen) {
      window.addEventListener('keydown', handleKeyDown);
      document.body.style.overflow = 'hidden';
    }
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
      document.body.style.overflow = '';
    };
  }, [isOpen, onClose]);

  if (!isOpen || !details) return null;

  const images = details.images && details.images.length > 0 ? details.images : [];

  const handlePrev = (e: React.MouseEvent) => {
    e.stopPropagation();
    setActiveImageIndex((prev) => (prev > 0 ? prev - 1 : images.length - 1));
  };

  const handleNext = (e: React.MouseEvent) => {
    e.stopPropagation();
    setActiveImageIndex((prev) => (prev < images.length - 1 ? prev + 1 : 0));
  };

  return (
    <div className="about-artist-modal-backdrop" onClick={onClose}>
      <div className="about-artist-modal" onClick={(e) => e.stopPropagation()}>
        <button
          className="about-artist-close-btn"
          onClick={onClose}
          aria-label="Close"
        >
          <CloseIcon />
        </button>

        {images.length > 0 ? (
          <div className="about-artist-hero">
            {images.map((imgUrl, idx) => (
              <img
                key={idx}
                src={imgUrl}
                alt={details.name}
                loading="eager"
                decoding="async"
                style={{
                  position: 'absolute',
                  inset: 0,
                  width: '100%',
                  height: '100%',
                  objectFit: 'cover',
                  opacity: idx === activeImageIndex ? 1 : 0,
                  transition: 'opacity 0.25s ease-in-out',
                  pointerEvents: idx === activeImageIndex ? 'auto' : 'none',
                }}
              />
            ))}

            {images.length > 1 && (
              <>
                <button
                  className="carousel-btn prev"
                  onClick={handlePrev}
                  aria-label="Previous photo"
                >
                  <PrevIcon />
                </button>
                <button
                  className="carousel-btn next"
                  onClick={handleNext}
                  aria-label="Next photo"
                >
                  <NextIcon />
                </button>
              </>
            )}
          </div>
        ) : null}

        <div className="about-artist-content">
          {/* Left Column: Stats */}
          <div className="about-stats-column">
            {details.worldRank ? (
              <div className="world-rank-badge">
                <span className="rank-number">#{details.worldRank}</span>
                <span className="rank-label">in the world</span>
              </div>
            ) : null}

            <div className="stat-group">
              <div className="stat-number">
                {details.followers.toLocaleString()}
              </div>
              <div className="stat-label">Followers</div>
            </div>

            <div className="stat-group">
              <div className="stat-number">
                {details.monthlyListeners.toLocaleString()}
              </div>
              <div className="stat-label">Monthly Listeners</div>
            </div>

            {details.topCities && details.topCities.length > 0 ? (
              <div className="top-cities-section">
                {details.topCities.map((c, i) => (
                  <div className="city-item" key={i}>
                    <div className="city-name">
                      {c.city}, {c.country}
                    </div>
                    <div className="city-listeners">
                      {c.listeners.toLocaleString()} listeners
                    </div>
                  </div>
                ))}
              </div>
            ) : null}

            <a
              className="report-link"
              href="#report"
              onClick={(e) => {
                e.preventDefault();
              }}
            >
              Report <ExternalLinkIcon />
            </a>
          </div>

          {/* Right Column: Bio and Verification */}
          <div className="about-bio-column">
            <div className="bio-text">
              {details.fullBio && details.fullBio.length > 0 ? (
                details.fullBio.map((paragraph, index) => (
                  <p key={index}>{paragraph}</p>
                ))
              ) : (
                <p>{details.bioSnippet}</p>
              )}
            </div>

            <div className="bio-source">
              ~ {details.bioSource || 'Wikipedia'}
            </div>

            <div className="verification-cards">
              <div className="verification-card">
                <div className="card-header-row">
                  <SpotifyVerifiedBadge size={22} />
                  <span>Verified by OpenMusic</span>
                </div>
                <div className="card-desc">
                  This artist has grown an active fanbase to be eligible for review and has met OpenMusic's criteria for profile authenticity.
                </div>
              </div>

              <div className="verification-card">
                <div className="card-header-row">
                  <span>Registered artist</span>
                </div>
                <div className="card-desc">
                  This artist has a claimed profile in OpenMusic for Artists.
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AboutArtistModal;
